import React, { useEffect, useState, useMemo } from 'react';
import { Subscription } from '../core/_models';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useToast } from '../../components/Toats';
import { cancelSubscription, suspendSubscription, reactivateSubscription, updateSubscription, getSubscriptions } from '../core/subscriptionService';
import { SubscriptionCancelModal } from './SubscriptionCancelModal';
import { SubscriptionSuspendModal } from './SubscriptionSuspendModal';
import { SubscriptionReactivateModal } from './SubscriptionReactivateModal';
import { SubscriptionDetailsModal } from './SubscriptionDetailsModal';
import { SubscriptionEditModal } from './SubscriptionEditModal';

import { getReactSelectStyles } from '../core/reactSelectTheme';
import { useThemeMode } from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos os Status' },
  { value: 'PROPOSTO', label: 'Proposto' },
  { value: 'NEGADO', label: 'Negado' },
  { value: 'PENDENTE', label: 'Pendente' },
  { value: 'ATIVO', label: 'Ativo' },
  { value: 'INADIMPLENTE', label: 'Inadimplente' },
  { value: 'SUSPENSO', label: 'Suspenso' },
  { value: 'CANCELADO', label: 'Cancelado' },
];

export const SubscriptionsTab: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const { toggleToast } = useToast();
  const { mode } = useThemeMode();
  const selectStyles = useMemo(() => getReactSelectStyles(), [mode]);

  const [selectedClient, setSelectedClient] = useState({ value: 'all', label: 'Todos os Clientes' });
  const [selectedPlan, setSelectedPlan] = useState({ value: 'all', label: 'Todos os Planos' });
  const [selectedStatus, setSelectedStatus] = useState({ value: 'all', label: 'Todos os Status' });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [cancelDate, setCancelDate] = useState<Date | null>(null);

  const clientOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'Todos os Clientes' }];
    const uniqueClients = new Map<string, string>();
    subscriptions.forEach((sub) => {
      if (sub.client) {
        const id = sub.client.id;
        const name = sub.client.user?.name || sub.client.nameResponsible || 'Desconhecido';
        uniqueClients.set(id, name);
      }
    });
    uniqueClients.forEach((name, id) => options.push({ value: id, label: name }));
    return options;
  }, [subscriptions]);

  const planOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'Todos os Planos' }];
    const uniquePlans = new Map<string, string>();
    subscriptions.forEach((sub) => {
      if (sub.plan) {
        const id = sub.plan.id;
        const name = sub.plan.name;
        uniquePlans.set(id, name);
      }
    });
    uniquePlans.forEach((name, id) => options.push({ value: id, label: name }));
    return options;
  }, [subscriptions]);

  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((sub) => {
      if (selectedClient.value !== 'all' && sub.client?.id !== selectedClient.value) return false;
      if (selectedPlan.value !== 'all' && sub.plan?.id !== selectedPlan.value) return false;
      if (selectedStatus.value !== 'all' && sub.status !== selectedStatus.value) return false;
      if (startDate) {
        if (!sub.start_date) return false;
        const subDate = new Date(sub.start_date);
        const startCompare = new Date(startDate);
        startCompare.setHours(0, 0, 0, 0);
        subDate.setHours(0, 0, 0, 0);
        if (subDate < startCompare) return false;
      }
      if (cancelDate) {
        if (!sub.cancelled_at) return false;
        const subDate = new Date(sub.cancelled_at);
        const cancelCompare = new Date(cancelDate);
        cancelCompare.setHours(0, 0, 0, 0);
        subDate.setHours(0, 0, 0, 0);
        if (subDate < cancelCompare) return false;
      }
      return true;
    });
  }, [subscriptions, selectedClient, selectedPlan, selectedStatus, startDate, cancelDate]);

  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const [showReactivate, setShowReactivate] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const fetchSubscriptions = () => {
    setLoading(true);
    getSubscriptions().then((data) => {
      setSubscriptions(data);
      setLoading(false);
    }).catch(() => {
      setSubscriptions([]);
      setLoading(false);
    });
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6 d-flex flex-column'>
        <div className='d-flex align-items-center mb-5'>
          <h3 className='card-title align-items-start flex-column m-0'>
            <span className='fw-bolder fs-3 mb-1'>Filtros de Busca</span>
          </h3>
        </div>

        <div className='row g-4 mb-4'>
          <div className='col-lg'>
            <label className='form-label fs-7 fw-bolder text-gray-700'>Cliente</label>
            <Select
              options={clientOptions}
              value={selectedClient}
              onChange={(val) => setSelectedClient(val as any)}
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Buscar cliente..."
              isSearchable
              styles={selectStyles}
            />
          </div>
          <div className='col-lg'>
            <label className='form-label fs-7 fw-bolder text-gray-700'>Plano Assinado</label>
            <Select
              options={planOptions}
              value={selectedPlan}
              onChange={(val) => setSelectedPlan(val as any)}
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Selecionar plano..."
              isSearchable
              styles={selectStyles}
            />
          </div>
          <div className='col-lg'>
            <label className='form-label fs-7 fw-bolder text-gray-700'>Status</label>
            <Select
              options={STATUS_OPTIONS}
              value={selectedStatus}
              onChange={(val) => setSelectedStatus(val as any)}
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder="Filtrar por status..."
              styles={selectStyles}
            />
          </div>
          <div className='col-lg'>
            <label className='form-label fs-7 fw-bolder text-gray-700'>Data de Início</label>
            <div className='input-group input-group-solid flex-nowrap'>
              <span className='input-group-text'><i className='bi bi-calendar'></i></span>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                className='form-control form-control-solid w-100'
                wrapperClassName='flex-grow-1'
                dateFormat='dd/MM/yyyy'
                placeholderText='DD/MM/AAAA'
                isClearable
              />
            </div>
          </div>
          <div className='col-lg'>
            <label className='form-label fs-7 fw-bolder text-gray-700'>Data de Cancelamento</label>
            <div className='input-group input-group-solid flex-nowrap'>
              <span className='input-group-text'><i className='bi bi-calendar-x'></i></span>
              <DatePicker
                selected={cancelDate}
                onChange={(date) => setCancelDate(date)}
                className='form-control form-control-solid w-100'
                wrapperClassName='flex-grow-1'
                dateFormat='dd/MM/yyyy'
                placeholderText='DD/MM/AAAA'
                isClearable
              />
            </div>
          </div>
        </div>
      </div>

      <div className='card-body py-4'>
        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Carregando...</span>
            </div>
          </div>
        ) : filteredSubscriptions.length === 0 ? (
          <div className='d-flex flex-column align-items-center justify-content-center py-15'>
            <i className='bi bi-inbox fs-2tx text-gray-300 mb-5'></i>
            <span className='text-gray-500 fw-bold fs-5'>Nenhuma assinatura encontrada.</span>
            <span className='text-muted fs-7 mt-2'>Ajuste os filtros ou cadastre novas assinaturas.</span>
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-5'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
                  <th className='min-w-150px'>Cliente</th>
                  <th className='min-w-125px'>Plano/Serviço</th>
                  <th className='min-w-125px'>Valor Mensal</th>
                  <th className='min-w-125px'>Status</th>
                  <th className='min-w-125px'>Data de Início</th>
                  <th className='text-end min-w-125px'>Ações</th>
                </tr>
              </thead>
              <tbody className='text-gray-600 fw-bold'>
                {filteredSubscriptions.map((sub) => (
                  <tr key={sub.id}>
                    <td className='d-flex align-items-center'>
                      <div className='d-flex flex-column'>
                        <a href='#' className='text-gray-800 text-hover-primary mb-1'>
                          {sub.client?.user?.name || sub.client?.nameResponsible || 'Desconhecido'}
                        </a>
                        <span className='text-muted fs-7'>ID: {sub.code}</span>
                      </div>
                    </td>
                    <td>
                      <span className='badge badge-light-primary fs-7 fw-bolder'>{sub.plan.name}</span>
                    </td>
                    <td>
                      R$ {sub.monthly_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                    <td>
                      <div className={`badge badge-light-${sub.status === 'ATIVO' ? 'success' : sub.status === 'INADIMPLENTE' ? 'danger' : 'warning'} fw-bolder`}>
                        {sub.status}
                      </div>
                    </td>
                    <td>{sub.start_date ? new Date(sub.start_date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Não iniciada'}</td>
                    <td className='text-end'>
                      <div className='btn-group' role='group'>
                        <button type='button' className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm' title='Detalhes' onClick={() => { setSelectedSubscription(sub); setShowDetails(true); }}>
                          <i className='bi bi-eye fs-4'></i>
                        </button>
                        {(sub.status === 'ATIVO' || sub.status === 'INADIMPLENTE' || sub.status === 'PROPOSTO' || sub.status === 'PENDENTE') && (
                          <button type='button' className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm' title='Editar' onClick={() => { setSelectedSubscription(sub); setShowEdit(true); }}>
                            <i className='bi bi-pencil fs-4'></i>
                          </button>
                        )}
                        {(sub.status === 'ATIVO' || sub.status === 'INADIMPLENTE') && (
                          <button type='button' className='btn btn-icon btn-bg-light btn-active-color-warning btn-sm' title='Suspender' onClick={() => { setSelectedSubscription(sub); setShowSuspend(true); }}>
                            <i className='bi bi-pause-circle fs-4'></i>
                          </button>
                        )}
                        {sub.status === 'SUSPENSO' && (
                          <button type='button' className='btn btn-icon btn-bg-light btn-active-color-success btn-sm' title='Reativar' onClick={() => { setSelectedSubscription(sub); setShowReactivate(true); }}>
                            <i className='bi bi-play-circle fs-4'></i>
                          </button>
                        )}
                        {(sub.status === 'PROPOSTO' || sub.status === 'PENDENTE' || sub.status === 'ATIVO') && (
                          <button type='button' className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm' title='Cancelar' onClick={() => { setSelectedSubscription(sub); setShowCancel(true); }}>
                            <i className='bi bi-x-circle fs-4'></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && filteredSubscriptions.length > 0 && (
        <div className='card-footer border-0 pt-0 pb-5 d-flex justify-content-between align-items-center'>
          <div className='text-gray-500 fw-bold fs-6'>
            Mostrando 1-{filteredSubscriptions.length} de {filteredSubscriptions.length} assinaturas
          </div>
          <ul className='pagination'>
            <li className='page-item previous disabled'><a href='#' className='page-link'><i className='previous'></i></a></li>
            <li className='page-item active'><a href='#' className='page-link'>1</a></li>
            <li className='page-item next disabled'><a href='#' className='page-link'><i className='next'></i></a></li>
          </ul>
        </div>
      )}

      <SubscriptionDetailsModal
        subscription={selectedSubscription}
        show={showDetails}
        onHide={() => { setShowDetails(false); setSelectedSubscription(null); }}
      />
      <SubscriptionEditModal
        subscription={selectedSubscription}
        show={showEdit}
        isLoading={isActionLoading}
        onHide={() => { setShowEdit(false); setSelectedSubscription(null); }}
        onConfirm={(id, payload) => {
          setIsActionLoading(true);
          updateSubscription(id, payload).then(() => {
            toggleToast('Assinatura atualizada com sucesso', 'success');
            fetchSubscriptions();
            setShowEdit(false);
            setSelectedSubscription(null);
          }).catch(err => {
            toggleToast(err.response?.data?.error || 'Erro ao atualizar', 'danger');
          }).finally(() => setIsActionLoading(false));
        }}
      />
      <SubscriptionCancelModal
        subscription={selectedSubscription}
        show={showCancel}
        isLoading={isActionLoading}
        onHide={() => { setShowCancel(false); setSelectedSubscription(null); }}
        onConfirm={(id, reason) => {
          setIsActionLoading(true);
          cancelSubscription(id, reason).then(() => {
            toggleToast('Assinatura cancelada com sucesso', 'success');
            fetchSubscriptions();
            setShowCancel(false);
            setSelectedSubscription(null);
          }).catch(err => {
            toggleToast(err.response?.data?.error || 'Erro ao cancelar', 'danger');
          }).finally(() => setIsActionLoading(false));
        }}
      />
      <SubscriptionSuspendModal
        subscription={selectedSubscription}
        show={showSuspend}
        isLoading={isActionLoading}
        onHide={() => { setShowSuspend(false); setSelectedSubscription(null); }}
        onConfirm={(id) => {
          setIsActionLoading(true);
          suspendSubscription(id).then(() => {
            toggleToast('Assinatura suspensa com sucesso', 'success');
            fetchSubscriptions();
            setShowSuspend(false);
            setSelectedSubscription(null);
          }).catch(err => {
            toggleToast(err.response?.data?.error || 'Erro ao suspender', 'danger');
          }).finally(() => setIsActionLoading(false));
        }}
      />
      <SubscriptionReactivateModal
        subscription={selectedSubscription}
        show={showReactivate}
        isLoading={isActionLoading}
        onHide={() => { setShowReactivate(false); setSelectedSubscription(null); }}
        onConfirm={(id) => {
          setIsActionLoading(true);
          reactivateSubscription(id).then(() => {
            toggleToast('Assinatura reativada com sucesso', 'success');
            fetchSubscriptions();
            setShowReactivate(false);
            setSelectedSubscription(null);
          }).catch(err => {
            toggleToast(err.response?.data?.error || 'Erro ao reativar', 'danger');
          }).finally(() => setIsActionLoading(false));
        }}
      />
    </div>
  );
};
